import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BRAND } from "@/lib/config";
import { useTiers } from "@/lib/useTiers";
import { useAuth } from "@/lib/auth";
import CheckoutModal from "@/components/CheckoutModal";
import { Button } from "@/components/ui/button";
import { CheckCircle2 } from "lucide-react";

export default function Pricing() {
  const { user } = useAuth();
  const { tiers, loading } = useTiers();
  const [params] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [tier, setTier] = useState(null);

  const onClick = (key) => {
    if (!user) {
      try { localStorage.setItem("intended_tier", key); } catch {}
      window.location.href = `/register?next=pricing&tier=${encodeURIComponent(key)}`;
      return;
    }
    setTier(key); setOpen(true);
  };

  // Post-signup auto-resume: if the URL says ?checkout=<key> OR localStorage
  // has an intended_tier, and the user is now logged in, open the modal for
  // that tier automatically.
  useEffect(() => {
    if (!user || loading) return;
    let key = params.get("checkout") || params.get("tier");
    try {
      if (!key) key = localStorage.getItem("intended_tier") || "";
    } catch {}
    if (!key) return;
    const match = tiers.find((t) => (t.tier_key || t.key) === key);
    if (!match) return;
    // If they already have this level of access, skip
    setTier(key);
    setOpen(true);
    try { localStorage.removeItem("intended_tier"); } catch {}
  }, [user, loading, tiers, params]);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <nav className="fixed top-0 inset-x-0 z-40 glass">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-8 h-16">
          <Link to="/" className="flex items-center gap-3">
            <img src={BRAND.logo} className="h-9 w-9 rounded-md" alt="" />
            <span className="font-display text-xl">Dr Thrive 365</span>
          </Link>
          {user ? (
            <Link to="/dashboard" className="text-sm text-white/70 hover:text-white">Dashboard</Link>
          ) : (
            <Link to="/login" className="text-sm text-white/70 hover:text-white">Sign in</Link>
          )}
        </div>
      </nav>
      <section className="pt-32 pb-24 px-4 sm:px-8 max-w-7xl mx-auto">
        <div className="text-[11px] tracking-[0.25em] uppercase text-[#FF8A00] mb-3">Pricing</div>
        <h1 className="font-display text-5xl sm:text-6xl">Choose your entry point.</h1>
        <p className="text-white/55 mt-3 max-w-xl">Upgrade any time — everything you've unlocked stays unlocked.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {tiers.map((t) => {
            const current = user?.tier === t.grants_tier;
            return (
              <div key={t.key} data-testid={`pricing-tier-${t.key}`}
                className={`relative rounded-2xl p-6 border flex flex-col ${
                  t.popular ? "border-[#FF8A00]/40 bg-gradient-to-b from-[#FF8A00]/10 to-transparent"
                            : "border-white/8 bg-[#0A0A0A]"
                }`}>
                {t.popular && (
                  <div className="absolute -top-3 left-6 text-[10px] tracking-[0.22em] uppercase bg-gradient-to-r from-[#FF8A00] to-[#E52E71] px-3 py-1 rounded-full">
                    Most loved
                  </div>
                )}
                <div className="font-display text-xl mb-1">{t.name}</div>
                <div className="text-xs text-white/50 mb-5">{t.sub}</div>
                <div className="font-display text-4xl">{t.price}<span className="text-xs text-white/40 ml-2">{t.cadence}</span></div>
                <ul className="mt-6 space-y-2 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-white/75">
                      <CheckCircle2 size={14} className="mt-0.5 text-[#FF8A00] shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => onClick(t.key)}
                  disabled={current}
                  data-testid={`choose-${t.key}`}
                  className={`mt-6 rounded-full ${
                    t.popular
                      ? "bg-gradient-to-r from-[#FF8A00] to-[#E52E71] hover:opacity-90"
                      : "bg-white/15 border border-white/20 text-white hover:bg-white/25"
                  }`}>
                  {current ? "Current tier" : (t.popular ? "Get started" : `Pay ${t.price} · ${t.cadence}`)}
                </Button>
              </div>
            );
          })}
        </div>
      </section>
      <CheckoutModal open={open} onOpenChange={setOpen} tierKey={tier} />
    </div>
  );
}
